// src/gates/RequireAuth.js
import { useAuth } from "react-oidc-context";
import { Navigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { Box, CircularProgress } from "@mui/material";

export default function RequireAuth({ children }) {
  const auth = useAuth();

  const [silentChecked, setSilentChecked] = useState(false);
  const silentAttempted = useRef(false);
  const signoutBroadcast = useRef(false);

  const expired = auth.user?.expired ?? false;
  const hasUser = !!auth.user;
  const token = auth.user?.access_token ?? null;

  const logoutInProgress =
    typeof window !== "undefined" &&
    sessionStorage.getItem("logoutInProgress") === "1";

  useEffect(() => {
    if (auth.isLoading) return;
    if (silentAttempted.current) return;

    silentAttempted.current = true;

    const trySilentRefresh = async () => {
      if (!auth.user) {
        setSilentChecked(true);
        return;
      }

      if (!expired) {
        setSilentChecked(true);
        return;
      }

      try {
        console.log("🔄 Attempting signinSilent...");
        await Promise.race([
          auth.signinSilent(),
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error("silent timeout")), 1500),
          ),
        ]);
        console.log("✅ signinSilent succeeded");
      } catch (e) {
        console.error("❌ signinSilent failed, removing user:", e);
        auth.removeUser();
        localStorage.removeItem("login_intent");
        localStorage.removeItem("pending_price_id");
        localStorage.removeItem("promo_top_soft_v1");
      } finally {
        setSilentChecked(true);
      }
    };

    trySilentRefresh();
  }, [auth.isLoading, expired, auth]);

  useEffect(() => {
    const { events } = auth;
    const onUserUnloaded = () => {
      signoutBroadcast.current = true;
      setTimeout(() => {
        signoutBroadcast.current = false;
      }, 100);
    };
    events.addUserUnloaded(onUserUnloaded);
    return () => events.removeUserUnloaded(onUserUnloaded);
  }, [auth]);

  // ⏳ Always show spinner until we've fully resolved auth state
  // ✅ Removed early redirect — it was racing with silentChecked=false
  const stillBooting =
    auth.isLoading ||
    !silentChecked ||
    signoutBroadcast.current ||
    logoutInProgress;

  if (stillBooting) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "60vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // ✅ Only evaluate auth after fully booted — no flash possible
  const isAuthed = auth.isAuthenticated && hasUser && token && !expired;

  if (!isAuthed) {
    localStorage.setItem("login_intent", "from_header");
    return <Navigate to="/login" replace />;
  }

  return children;
}
